import { deleteProject, useUpdateProjectSettings } from '@api/settings';
import { ActionsFooter } from '@components/Forms';
import ApprovalDialogButton from '@components/Forms/ApprovalDialog/ApprovalDialogButton';
import DeleteDialogButton from '@components/Forms/DeleteDialog/DeleteDialogButton';
import IntervalSelect from '@components/IntervalSelect/IntervalSelect';
import SettingsSection from '@components/ProjectSettings/SettingsSection';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import HelpTooltip from '@components/Tooltips/HelpTooltip';
import { LoadingButton } from '@mui/lab';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	Link,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { AnalyticsActionName } from '@utils/analytics';
import { HELP_LINKS, SUPPORT_MAIL } from '@utils/docs';
import { onSubmitForm, resetFormikField } from '@utils/forms';
import {
	numericIntervalValidationScheme,
	stringRequiredValidationScheme,
	stringRequiredWhenFieldEqualsValidationScheme,
	urlValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import useAnalytics from 'hooks/useAnalytics';
import { AuthContext } from 'hooks/useAuth';
import { compact } from 'lodash';
import React, { FC, useContext } from 'react';
import { IProjectData, ProjectContext } from 'routes/ProjectProvider';
import { TimeUnit, TokenResponseMethod } from 'types/common';
import { IProjectSettings } from 'types/settings';
import { object, string } from 'yup';

const FORM_KEYS = {
	projectId: 'projectId',
	projectName: 'projectName',
	trustedDomains: 'trustedDomains',
	inviteURL: 'inviteURL',
	projectSelfProvisioning: 'projectSelfProvisioning',
	domain: 'domain',
	refreshTokenExpiration: 'refreshTokenExpiration',
	refreshTokenExpirationUnit: 'refreshTokenExpirationUnit',
	stepupTokenExpiration: 'stepupTokenExpiration',
	stepupTokenExpirationUnit: 'stepupTokenExpirationUnit',
	tokenResponseMethod: 'tokenResponseMethod'
};
const DEFAULT_FORM: IProjectSettings = {
	[FORM_KEYS.projectId]: '',
	[FORM_KEYS.projectName]: '',
	[FORM_KEYS.inviteURL]: '',
	[FORM_KEYS.trustedDomains]: '',
	[FORM_KEYS.projectSelfProvisioning]: false,
	[FORM_KEYS.domain]: '',
	[FORM_KEYS.refreshTokenExpiration]: 0,
	[FORM_KEYS.stepupTokenExpiration]: 10,
	[FORM_KEYS.stepupTokenExpirationUnit]: 'minutes',
	[FORM_KEYS.tokenResponseMethod]: TokenResponseMethod.SessionCookies
} as unknown as IProjectSettings;

const ProjectSettingsPage: FC<{ data?: IProjectSettings }> = ({ data }) => {
	const { addToast } = useToast();
	const [tokenResponseDialog, setTokenResponseDialog] = React.useState(false);
	const updateSettings = useUpdateProjectSettings();
	const { checkLogin } = useContext(AuthContext);
	const { invokeToastableAction } = useToast();
	const track = useAnalytics();

	const initialValues = React.useMemo(
		() => ({
			...DEFAULT_FORM,
			...data
		}),
		[data]
	);

	const { setCurrentProject, setCurrentProjectStorage, setCurrentCompany } =
		React.useContext<IProjectData>(ProjectContext);

	const formik = useFormik<IProjectSettings>({
		enableReinitialize: true,
		initialValues,
		validationSchema: object({
			[FORM_KEYS.projectName]: stringRequiredValidationScheme(),
			[FORM_KEYS.refreshTokenExpiration]: numericIntervalValidationScheme(),
			[FORM_KEYS.stepupTokenExpiration]: numericIntervalValidationScheme(100),
			[FORM_KEYS.trustedDomains]: string(),
			[FORM_KEYS.inviteURL]: urlValidationScheme(),
			[FORM_KEYS.domain]: stringRequiredWhenFieldEqualsValidationScheme(
				FORM_KEYS.tokenResponseMethod,
				TokenResponseMethod.SessionCookies
			)
		}),
		onSubmit: onSubmitForm<IProjectSettings>(async (newData) => {
			const res = await updateSettings.mutateAsync(newData);
			track(AnalyticsActionName.projectSettings, res);

			if (newData.projectName !== initialValues?.projectName) {
				checkLogin();
			}
			return res;
		}, addToast)
	});

	const onDelete = React.useCallback(
		async () =>
			invokeToastableAction(
				deleteProject,
				'projectSettings.delete.project',
				() => {
					setCurrentProjectStorage('');
					setCurrentProject('');
					setCurrentCompany('');

					track(AnalyticsActionName.projectSettingsDelete, formik.values);

					window.location.reload();
				}
			),
		[
			invokeToastableAction,
			setCurrentProjectStorage,
			setCurrentProject,
			setCurrentCompany,
			track,
			formik.values
		]
	);

	const trustedDomains = compact(
		(formik.values?.trustedDomains || '').split(',')
	);

	return (
		<>
			<Box display="flex" flexGrow={1} flexDirection="column">
				<SettingsSection divider>
					<Grid container spacing={3}>
						<Grid item xs={12} md={3}>
							<TextFieldValidation
								id={FORM_KEYS.projectName}
								formik={formik}
								fullWidth
								required
								label={intl.tr(`projectSettings.${FORM_KEYS.projectName}`)}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<TextFieldValidation
								id={FORM_KEYS.projectId}
								formik={formik}
								copy
								label={intl.tr(`projectSettings.${FORM_KEYS.projectId}`)}
								disabled
								fullWidth
							/>
						</Grid>
					</Grid>
				</SettingsSection>
				<SettingsSection
					title={intl.tr('projectSettings.section.security.header')}
					divider
				>
					<Grid
						container
						spacing={2}
						alignItems="flex-start"
						position="relative"
					>
						<Grid item xs={12} md={6}>
							<Box flexDirection="row" display="flex" pb={1}>
								<Autocomplete
									data-testid="project-settings-trusted-domains"
									multiple
									filterSelectedOptions
									freeSolo
									autoSelect
									id={FORM_KEYS.trustedDomains}
									value={trustedDomains}
									options={trustedDomains}
									renderInput={(params: any) => (
										<TextField
											{...params}
											label={intl.tr('projectSettings.trustedDomains')}
											placeholder={intl.tr(
												'projectSettings.domain.placeholder'
											)}
											error={!!formik.errors.trustedDomains}
											helperText={formik.errors.trustedDomains}
										/>
									)}
									onChange={async (_: any, v: any) => {
										let output = v;
										if (v && Array.isArray(v)) {
											output = v.join(',');
										}
										await formik.setFieldValue(
											FORM_KEYS.trustedDomains,
											output
										);
									}}
								/>
							</Box>
							{(!trustedDomains || !trustedDomains.length) && (
								<Alert severity="warning" data-testid="trusted-domain-alert">
									{intl.tr('projectSettings.trustedDomains.warn')}
								</Alert>
							)}
						</Grid>
					</Grid>
				</SettingsSection>
				<SettingsSection
					title={intl.tr('projectSettings.section.signup.header')}
					divider
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<FormControlLabel
								id="project-settings-signup-checkbox"
								label={intl.tr(
									`projectSettings.${FORM_KEYS.projectSelfProvisioning}`
								)}
								control={
									<Checkbox
										id={FORM_KEYS.projectSelfProvisioning}
										data-testid="project-settings-signup-checkbox"
										checked={!formik.values.projectSelfProvisioning}
										onChange={() =>
											formik.setFieldValue(
												FORM_KEYS.projectSelfProvisioning,
												!formik.values.projectSelfProvisioning
											)
										}
									/>
								}
							/>
						</Grid>
						<Grid item container xs={12} rowSpacing={2} alignItems="flex-start">
							<Grid item xs={10} md={6}>
								<TextFieldValidation
									id={FORM_KEYS.inviteURL}
									formik={formik}
									fullWidth
									label={intl.tr(`projectSettings.${FORM_KEYS.inviteURL}`)}
								/>
							</Grid>
							<Grid item xs={1} pl={1} mt={1} display="flex">
								<HelpTooltip
									title={intl.tr(
										`projectSettings.${FORM_KEYS.inviteURL}.tooltip`
									)}
									placement="bottom"
								/>
							</Grid>
						</Grid>
					</Grid>
				</SettingsSection>
				<SettingsSection
					title={intl.tr('projectSettings.section.session.management.header')}
				>
					<>
						<Grid container spacing={2} alignItems="flex-start">
							<Grid item xs={5} md={3}>
								<TextFieldValidation
									id={FORM_KEYS.refreshTokenExpiration}
									formik={formik}
									label={intl.tr(
										`projectSettings.${FORM_KEYS.refreshTokenExpiration}`
									)}
									type="number"
									required
									fullWidth
									InputProps={{ inputProps: { min: 1 } }}
								/>
							</Grid>
							<Grid item xs={5} md={3}>
								<IntervalSelect
									options={[TimeUnit.minutes, TimeUnit.days, TimeUnit.weeks]}
									initialValue={
										formik.values.refreshTokenExpirationUnit || TimeUnit.weeks
									}
									onChange={formik.handleChange}
									name={FORM_KEYS.refreshTokenExpirationUnit}
								/>
							</Grid>
							<Grid item xs={2} pl={1} mt={1} display="flex">
								<HelpTooltip
									title={intl.tr(
										'projectSettings.refreshTokenExpiration.tooltip'
									)}
									placeholder="right"
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2} alignItems="flex-start" mt={2}>
							<Grid item xs={5} md={3}>
								<TextFieldValidation
									id={FORM_KEYS.stepupTokenExpiration}
									formik={formik}
									label={intl.tr(
										`projectSettings.${FORM_KEYS.stepupTokenExpiration}`
									)}
									type="number"
									required
									fullWidth
									InputProps={{ inputProps: { min: 1, max: 100 } }}
								/>
							</Grid>
							<Grid item xs={5} md={3}>
								<IntervalSelect
									options={[TimeUnit.minutes]}
									initialValue={
										formik.values.stepupTokenExpirationUnit || TimeUnit.minutes
									}
									onChange={formik.handleChange}
									name={FORM_KEYS.stepupTokenExpirationUnit}
									disabled
								/>
							</Grid>
							<Grid item xs={2} md={1} pl={1} mt={1} display="flex">
								<HelpTooltip
									title={intl.tr(
										'projectSettings.stepupTokenExpiration.tooltip'
									)}
									placeholder="right"
								/>
							</Grid>
						</Grid>
						<Grid
							container
							spacing={2}
							alignItems="flex-start"
							position="relative"
							mt={2}
						>
							<Grid item xs={12} mt={3}>
								<FormControl>
									<FormLabel id="token-response-radio-buttons-group">
										{intl.tr(
											'projectSettings.fields.tokenResponseMethod.title'
										)}
									</FormLabel>
									<Typography variant="caption">
										{intl.tr(
											'projectSettings.fields.tokenResponseMethod.subtitle'
										)}
									</Typography>
									<Grid item xs={12}>
										<Button
											variant="text"
											color="info"
											size="small"
											sx={{
												marginTop: '6px'
											}}
											onClick={() => {
												setTokenResponseDialog(true);
											}}
										>
											<Typography variant="caption">
												{intl.tr(
													'projectSettings.fields.tokenResponseMethod.button'
												)}
											</Typography>
										</Button>
									</Grid>
									<RadioGroup
										aria-labelledby="token-response-radio-buttons-group"
										name={FORM_KEYS.tokenResponseMethod}
										onChange={formik.handleChange}
										value={formik.values.tokenResponseMethod}
									>
										<FormControlLabel
											value={TokenResponseMethod.SessionCookies}
											control={<Radio />}
											label={intl.tr(
												'projectSettings.fields.tokenResponseMethod.SessionCookies'
											)}
										/>
										{formik.values.tokenResponseMethod ===
											TokenResponseMethod.SessionCookies && (
											<Stack p={2} pl={4}>
												<TextFieldValidation
													id={FORM_KEYS.domain}
													formik={formik}
													sx={{ width: { sx: '100%', sm: '50%' } }}
													required
													placeholder={intl.tr(
														`projectSettings.${FORM_KEYS.domain}.placeholder`
													)}
													label={intl.tr(`projectSettings.${FORM_KEYS.domain}`)}
												/>
												<Alert
													severity="warning"
													sx={{
														whiteSpace: 'pre-line',
														alignItems: 'center',
														mt: 2
													}}
													data-testid="custom-domain-alert"
												>
													{intl.tr('projectSettings.customDomain.warn')}
													<Link href={`mailto:${SUPPORT_MAIL}`}>
														{intl.tr(
															'projectSettings.customDomain.warn.successTeam'
														)}
													</Link>
													{intl.tr('projectSettings.customDomain.warn2')}
													<Link
														href={`${HELP_LINKS.documentation}/manage/session/`}
														target="_blank"
													>
														{intl.tr('projectSettings.customDomain.warn.docs')}
													</Link>
												</Alert>
											</Stack>
										)}
										<FormControlLabel
											value={TokenResponseMethod.TokenInBody}
											control={<Radio />}
											label={intl.tr(
												'projectSettings.fields.tokenResponseMethod.TokenInBody'
											)}
											onClick={async () => {
												await resetFormikField(formik, FORM_KEYS.domain);
											}}
										/>
									</RadioGroup>
									<Dialog open={tokenResponseDialog}>
										<DialogTitle>
											{intl.tr(
												'projectSettings.fields.tokenResponseMethod.title'
											)}
										</DialogTitle>
										<DialogContent data-testid="token-response-method-dialog">
											<DialogContentText>
												<Box component="span" whiteSpace="break-spaces">
													{intl.tr(
														'projectSettings.fields.tokenResponseMethod.dialog.content'
													)}
												</Box>
											</DialogContentText>
										</DialogContent>
										<DialogActions>
											<Button
												onClick={() => setTokenResponseDialog(false)}
												data-testid="token-response-dialog-cancel-button"
											>
												{intl.tr('button.close')}
											</Button>
										</DialogActions>
									</Dialog>
								</FormControl>
							</Grid>
						</Grid>
					</>
				</SettingsSection>
			</Box>
			<ActionsFooter>
				<LoadingButton
					color="primary"
					variant="contained"
					loading={formik.isSubmitting}
					disabled={!formik.dirty}
					sx={{ marginRight: 1 }}
					onClick={() => formik.handleSubmit()}
				>
					{intl.tr('button.save')}
				</LoadingButton>
				<ApprovalDialogButton
					buttonText={intl.tr('button.cancel')}
					onApprove={formik.resetForm}
					disabled={!formik.dirty}
					title={intl.tr('discard.changes.dialog.title')}
					question={intl.tr('discard.changes.dialog.question')}
					approveText={intl.tr('discard.changes.dialog.approve')}
				/>
				<Box display="block" flexGrow={1}>
					<Stack direction="row" justifyContent="end">
						<DeleteDialogButton
							onDelete={onDelete}
							buttonText={intl.tr('projectSettings.dialog.env.del.title')}
							title={intl.tr('projectSettings.dialog.env.del.title')}
							question={intl.tr('projectSettings.dialog.env.del.question.1', {
								envName: formik.values.projectName
							})}
							extraVerification
							name={formik.values.projectName}
							inputHeader={intl.tr('projectSettings.dialog.env.del.question.2')}
							inputText={intl.tr(
								'projectSettings.delete.dialog.input.name.label'
							)}
							color="error"
						/>
					</Stack>
				</Box>
			</ActionsFooter>
		</>
	);
};

ProjectSettingsPage.defaultProps = {
	data: undefined
};

export default ProjectSettingsPage;
